/** Insights → Templates → Dashboard - where the tab opens: every template's
 *  results side by side, for the date range and scope above. One request
 *  answers them all (GET /insights/templates/dashboard).
 *
 *  A summary row first (templates on, answers in the date range - named -
 *  couldn't be answered, the last run) and, when the range leaves answers
 *  out, how many with Show all; then one card per template, every card the
 *  same shape and height, the plain card of the rest of Insights (item 78:
 *  no accent band): the name, its answer type, its schedule's "Repeats" mark
 *  and last run, Open; the takeaway (what most people answered - never on
 *  too few answers or a tie); the chart, compact, each category in its own
 *  colour (item 70 - data colours); what the range leaves out; when its
 *  answers were written and how many conversations it could not answer.
 *  Templates with answers come first, then those without, switched-off ones
 *  last. A click anywhere on a card (or Enter / Space on it, or Open) opens
 *  that template on its own. The first
 *  load shows skeleton cards; a refresh keeps the cards and dims them until
 *  the new numbers arrive. */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { BarChartOutlined, RightOutlined, SyncOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { formatRelativeTime } from '@/utils/relativeTime';
import insightsService from '../../services/insightsService';
import type { InsightFilters, InsightTemplate, LabelAggregation } from '../../types/insights';
import { outsideOf, type RangeFacts, type Span } from '../../utils/templateRange';
import { OutsideRange, UpdatedAt, rangeLabel } from './RangeParts';
import { CouldntWhy, addSplits } from './CouldntWhy';
import { ScheduleMark } from './ScheduleMark';
import { Takeaway, TemplateChart } from './TemplateChart';
import { TYPE_ICON, errorMessage, filterParams, formatCount, scaleRange, typeName } from './templateParts';

interface Props {
  /** In the list's order. */
  templates: InsightTemplate[];
  filters: InsightFilters;
  /** Changes when answers may have changed (a run moved or finished, a template saved). */
  refreshKey: string;
  /** On screen (it stays mounted while one template is open): it reads only
      while shown, and catches up once when shown again. */
  shown: boolean;
  isRunning: (templateId: string) => boolean;
  /** Open one template on its own. */
  onOpen: (templateId: string) => void;
  /** The numbers arrived (the page may open its range on the answers). */
  onLoaded: (facts: RangeFacts[]) => void;
  /** Show all: widen the range to these days. */
  onShowAll: (span: Span | null) => void;
}

const SKELETON_CARDS = 6;

export function TemplatesDashboard({
  templates, filters, refreshKey, shown, isRunning, onOpen, onLoaded, onShowAll,
}: Props) {
  const { t } = useLanguage();
  const [aggregations, setAggregations] = useState<Record<string, LabelAggregation> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtersKey = JSON.stringify(filters);
  const templatesKey = templates.map((x) => x.template_id).join(',');
  // What the numbers on the cards were read for: nothing is read again while
  // hidden, nor when shown again for the same filters and answers.
  const readFor = useRef<string | null>(null);
  useEffect(() => {
    const key = `${filtersKey}|${templatesKey}|${refreshKey}`;
    if (!shown || readFor.current === key) return;
    readFor.current = key;
    let cancelled = false;
    let done = false;
    setLoading(true);
    setError(null);
    insightsService.getTemplatesDashboard(filterParams(filters))
      .then((rows) => {
        if (cancelled) return;
        setAggregations(Object.fromEntries(rows.map((r) => [r.template_id, r.aggregation])));
        onLoaded(rows.map((r) => r.aggregation));
      })
      .catch((err) => { if (!cancelled) { readFor.current = null; setError(errorMessage(t, err)); } })
      .finally(() => { done = true; if (!cancelled) setLoading(false); });
    // A read cut short (filters changed, or hidden) is read again when it matters.
    return () => { cancelled = true; if (!done) readFor.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, templatesKey, refreshKey, shown]);

  // What has something to say first; switched-off templates last. The list's order within each.
  const ordered = useMemo(() => {
    const rank = (tpl: InsightTemplate) => {
      if (!tpl.is_active) return 2;
      return (aggregations?.[tpl.template_id]?.total || 0) > 0 ? 0 : 1;
    };
    return [...templates].sort((a, b) => rank(a) - rank(b));   // a stable sort keeps the list's order
  }, [templates, aggregations]);

  if (error) return <p className="tpl-error" role="alert">{error}</p>;

  if (!aggregations) {
    return (
      <div className="tpl-board" aria-busy="true">
        <div className="tpl-kpis tpl-kpis--skeleton" aria-hidden="true">
          {Array.from({ length: 4 }, (_, i) => <div key={i} className="tpl-kpi tpl-skel" />)}
        </div>
        <ul className="tpl-dash" aria-hidden="true">
          {Array.from({ length: Math.min(SKELETON_CARDS, templates.length || SKELETON_CARDS) }, (_, i) => (
            <li key={i} className="tpl-dash__card tpl-dash__card--skeleton">
              <span className="tpl-skel tpl-skel--line" />
              <span className="tpl-skel tpl-skel--short" />
              <span className="tpl-skel tpl-skel--chart" />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const on = templates.filter((x) => x.is_active).length;
  const answers = templates.reduce((sum, x) => sum + (aggregations[x.template_id]?.total || 0), 0);
  const couldnt = templates.reduce((sum, x) => sum + (aggregations[x.template_id]?.unanswered || 0), 0);
  const couldntWhy = addSplits(templates.map((x) => aggregations[x.template_id]?.unanswered_split));
  const lastRun = templates.map((x) => x.last_run_at).filter(Boolean).sort().pop() || null;
  const running = templates.filter((x) => isRunning(x.template_id)).length;
  const left = outsideOf(templates.map((x) => aggregations[x.template_id] || {}));
  // A click anywhere on a card opens it - but a control on it (Open, Show all,
  // "more categories") does its own thing.
  const openCard = (templateId: string) => (e: MouseEvent<HTMLLIElement>) => {
    if (!e.currentTarget.contains(e.target as Node)) return;   // inside a tooltip (a portal)
    if (!(e.target as HTMLElement).closest('button, a, input')) onOpen(templateId);
  };
  const openByKey = (templateId: string) => (e: KeyboardEvent<HTMLLIElement>) => {
    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onOpen(templateId);
    }
  };

  return (
    <div className={`tpl-board${loading ? ' is-refreshing' : ''}`} aria-busy={loading || undefined}>
      <dl className="tpl-kpis" data-testid="templates-summary">
        <div className="tpl-kpi">
          <dt>{t('insights.tpl.dash.kpiOn')}</dt>
          <dd>{t('insights.tpl.dash.kpiOnValue', { on: formatCount(on), all: formatCount(templates.length) })}</dd>
        </div>
        <div className="tpl-kpi">
          <dt>{t('insights.tpl.dash.kpiAnswersIn', { range: rangeLabel(filters) })}</dt>
          <dd><bdi>{formatCount(answers)}</bdi></dd>
        </div>
        <div className="tpl-kpi">
          <dt>
            <CouldntWhy split={couldntWhy} total={couldnt} all testId="templates-couldnt-info">{t('insights.tpl.dash.kpiCouldnt')}</CouldntWhy>
          </dt>
          <dd><bdi>{formatCount(couldnt)}</bdi></dd>
        </div>
        <div className="tpl-kpi">
          <dt>{t('insights.tpl.dash.kpiLastRun')}</dt>
          <dd>
            {running > 0
              ? <span className="tpl-dash__running"><SyncOutlined spin aria-hidden />
                {t('insights.tpl.dash.kpiRunning', { count: running, n: formatCount(running) })}</span>
              : lastRun ? formatRelativeTime(lastRun) : t('insights.tpl.list.notRunYet')}
          </dd>
        </div>
      </dl>
      <OutsideRange outside={left.outside} onShowAll={() => onShowAll(left.span)} testId="templates-outside" />

      <span id="tpl-dash-open-hint" className="tpl-sr">{t('insights.tpl.dash.cardHint')}</span>
      <ul className="tpl-dash" data-testid="templates-dashboard">
        {ordered.map((tpl) => {
          const aggregation = aggregations[tpl.template_id];
          const answered = aggregation?.total || 0;
          const unanswered = aggregation?.unanswered || 0;
          const busy = isRunning(tpl.template_id);
          const neverRun = !tpl.last_run_at && tpl.total_results_count === 0;
          const titleId = `tpl-dash-${tpl.template_id}`;
          const range = scaleRange(tpl);
          return (
            <li key={tpl.template_id} className={`tpl-dash__card${tpl.is_active ? '' : ' is-off'}`}
                tabIndex={0} onClick={openCard(tpl.template_id)} onKeyDown={openByKey(tpl.template_id)}
                aria-labelledby={titleId} aria-describedby="tpl-dash-open-hint"
                data-testid={`template-card-${tpl.template_id}`}>
              <header className="tpl-dash__head">
                <span className="tpl-row__icon" aria-hidden="true">{TYPE_ICON[tpl.response_type]}</span>
                <span className="tpl-dash__titles">
                  <span className="tpl-dash__title-row">
                    <h3 id={titleId} className="tpl-dash__title" dir="auto" title={tpl.template_name}>{tpl.template_name}</h3>
                    <ScheduleMark template={tpl} compact />
                    {!tpl.is_active && <span className="cx-pill">{t('insights.tpl.list.off')}</span>}
                  </span>
                  <span className="tpl-dash__meta">
                    <span>{typeName(t, tpl.response_type)}</span>
                    <span aria-hidden="true">·</span>
                    {busy ? (
                      <span className="tpl-dash__running"><SyncOutlined spin aria-hidden />{t('insights.tpl.list.analyzing')}</span>
                    ) : (
                      <span>{tpl.last_run_at
                        ? t('insights.tpl.list.lastRun', { when: formatRelativeTime(tpl.last_run_at) })
                        : t('insights.tpl.list.notRunYet')}</span>
                    )}
                  </span>
                </span>
                <button type="button" className="cx-btn cx-btn--sm tpl-dash__open" onClick={() => onOpen(tpl.template_id)}
                        aria-label={t('insights.tpl.dash.openNamed', { name: tpl.template_name })}>
                  {t('insights.tpl.dash.open')}<RightOutlined aria-hidden className="tpl-dash__chevron" />
                </button>
              </header>

              {answered > 0 && aggregation ? (
                <>
                  <div className="tpl-dash__take">
                    <Takeaway type={tpl.response_type} aggregation={aggregation} range={range} />
                    <span className="tpl-dash__answered">
                      {t('insights.tpl.results.answeredInRange', {
                        count: answered, n: formatCount(answered), range: rangeLabel(filters),
                      })}
                    </span>
                  </div>
                  <div className="tpl-dash__chart">
                    <TemplateChart type={tpl.response_type} aggregation={aggregation} view="chart" size="compact"
                                   range={range} onMore={() => onOpen(tpl.template_id)} />
                  </div>
                </>
              ) : (
                <div className="tpl-dash__empty">
                  <BarChartOutlined aria-hidden />
                  <span>{busy ? t('insights.tpl.results.answersArriving')
                    : !tpl.is_active && !aggregation?.outside ? t('insights.tpl.dash.switchedOff')
                      : neverRun ? t('insights.tpl.dash.neverRun')
                        : t('insights.tpl.results.noAnswersInRange', { range: rangeLabel(filters) })}</span>
                </div>
              )}

              <OutsideRange outside={aggregation?.outside || 0} onShowAll={() => onShowAll(aggregation?.span || null)}
                            testId={`template-card-outside-${tpl.template_id}`} />
              <p className="tpl-dash__foot">
                <UpdatedAt at={aggregation?.updated_at} />
                {unanswered > 0 && (
                  <CouldntWhy split={aggregation?.unanswered_split} total={unanswered}
                              testId={`template-card-couldnt-${tpl.template_id}`}>
                    {t('insights.tpl.results.couldnt', { count: unanswered, n: formatCount(unanswered) })}
                  </CouldntWhy>
                )}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
